import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';

const ForgotPassword = () => {
  const [step, setStep] = useState(1);
  const [mobileNumber, setMobileNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  
  const navigate = useNavigate();
  
  const { register, handleSubmit, formState: { errors } } = useForm();
  const { 
    register: registerReset, 
    handleSubmit: handleReset, 
    watch: watchReset, 
    formState: { errors: errorsReset } 
  } = useForm();

  // Timer for OTP resend
  React.useEffect(() => {
    if (otpTimer > 0) {
      const timer = setTimeout(() => setOtpTimer(otpTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [otpTimer]);

  // Step 1: Request OTP
  const onRequestOTP = async (data) => {
    setIsLoading(true);
    try {
      await axios.post('/api/auth/forgot-password', { 
        mobileNumber: data.mobileNumber 
      });
      setMobileNumber(data.mobileNumber);
      setStep(2);
      setOtpTimer(60); // 60 seconds timer for resend
      toast.success('OTP sent to your mobile number');
    } catch (err) {
      const errorMessage = err.response?.data?.error || 
        (err.response?.status === 404 ? 'Mobile number not found' : 
         'Error sending OTP');
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Resend OTP
  const handleResendOTP = async () => {
    if (otpTimer > 0) return;
    
    setIsLoading(true);
    try {
      await axios.post('/api/auth/forgot-password', { mobileNumber });
      setOtpTimer(60);
      toast.success('OTP resent successfully');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error resending OTP');
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Reset Password
  const onResetPassword = async (data) => {
    setIsLoading(true);
    try {
      await axios.post('/api/auth/reset-password', {
        mobileNumber,
        otp: data.otp,
        newPassword: data.newPassword,
        confirmPassword: data.confirmPassword
      });
      toast.success('Password reset successful! Redirecting to login...');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      const errorMessage = err.response?.data?.error || 
        (err.response?.status === 400 ? 'Invalid or expired OTP' : 
         'Error resetting password');
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-lg shadow-md">
        <div>
          <h2 className="text-2xl font-bold text-center text-gray-900 mb-2">
            Forgot Password
          </h2>
          <p className="text-center text-gray-600 text-sm">
            {step === 1 
              ? "Enter your mobile number to receive an OTP" 
              : "Enter the OTP sent to your mobile number"}
          </p>
        </div>

        {step === 1 ? (
          <form className="space-y-4" onSubmit={handleSubmit(onRequestOTP)}>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Mobile Number
              </label>
              <input
                {...register('mobileNumber', {
                  required: 'Mobile number is required',
                  pattern: {
                    value: /^[0-9]{10}$/,
                    message: 'Enter a valid 10-digit mobile number'
                  }
                })}
                type="tel"
                className="input-field mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="9876543210"
                disabled={isLoading}
              />
              {errors.mobileNumber && (
                <p className="text-red-600 text-sm mt-1">
                  {errors.mobileNumber.message}
                </p>
              )}
            </div>
            
            <button 
              type="submit" 
              className="w-full btn-primary bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLoading}
            >
              {isLoading ? 'Sending...' : 'Send OTP'}
            </button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="text-sm text-blue-600 hover:text-blue-500"
              >
                Back to Login
              </button>
            </div>
          </form>
        ) : (
          <form className="space-y-4" onSubmit={handleReset(onResetPassword)}>
            <div className="text-center mb-4">
              <p className="text-sm text-gray-600">
                OTP sent to: <span className="font-medium">{mobileNumber}</span>
              </p>
              <button
                type="button"
                onClick={() => {
                  setStep(1);
                  setOtpTimer(0);
                }}
                className="text-sm text-blue-600 hover:text-blue-500 mt-1"
              >
                ← Change number
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                OTP
              </label>
              <input
                {...registerReset('otp', { 
                  required: 'OTP is required',
                  pattern: {
                    value: /^[0-9]{4,6}$/,
                    message: 'Enter a valid OTP'
                  }
                })}
                type="text"
                className="input-field mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter OTP"
                disabled={isLoading}
              />
              {errorsReset.otp && (
                <p className="text-red-600 text-sm mt-1">
                  {errorsReset.otp.message}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                New Password
              </label>
              <input
                {...registerReset('newPassword', {
                  required: 'New password is required',
                  minLength: { 
                    value: 6, 
                    message: 'Password must be at least 6 characters' 
                  },
                  pattern: {
                    value: /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*#?&]{6,}$/,
                    message: 'Password must contain at least one letter and one number'
                  }
                })}
                type="password"
                className="input-field mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="New Password"
                disabled={isLoading}
              />
              {errorsReset.newPassword && (
                <p className="text-red-600 text-sm mt-1">
                  {errorsReset.newPassword.message}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Confirm Password
              </label>
              <input
                {...registerReset('confirmPassword', {
                  required: 'Please confirm your password',
                  validate: value => 
                    value === watchReset('newPassword') || 'Passwords do not match'
                })}
                type="password"
                className="input-field mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Confirm Password"
                disabled={isLoading}
              />
              {errorsReset.confirmPassword && (
                <p className="text-red-600 text-sm mt-1">
                  {errorsReset.confirmPassword.message}
                </p>
              )}
            </div>

            <button 
              type="submit" 
              className="w-full btn-primary bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLoading}
            >
              {isLoading ? 'Resetting...' : 'Reset Password'}
            </button>

            <div className="text-center">
              <button
                type="button"
                onClick={handleResendOTP}
                className={`text-sm ${
                  otpTimer > 0 
                    ? 'text-gray-400 cursor-not-allowed' 
                    : 'text-blue-600 hover:text-blue-500 cursor-pointer'
                }`}
                disabled={otpTimer > 0 || isLoading}
              >
                {otpTimer > 0 
                  ? `Resend OTP in ${otpTimer}s` 
                  : 'Resend OTP'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;